import { Id, Member, MemberAlias, MemberMap } from '@nextforce/api'
import { makeStyles } from '@rneui/themed'
import { MemberAliasSlugList } from 'chat/members/views/alias_slug_list'
import { MemberCard } from 'chat/members/views/card'
import { CollapsibleSectionHeader } from 'chat/members/views/collapsible_section_header'
import {
  MembersList,
  Section,
  categorizeMembers,
} from 'chat/members/views/list'
import { RecipientSearchBar } from 'chat/members/views/search_bar'
import { MemberSlugList } from 'chat/members/views/slug_list'
import React, { useCallback, useMemo, useState } from 'react'
import { SectionListData, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { toggleItemInObjectMap } from 'shared/utils'

interface Props {
  recipients: readonly Member[] | undefined
  selectedMembers: MemberMap
  disabledMemberIds?: Id[]
  isSingleSelectList?: boolean
  searchText: string
  onMembersSelected(members: MemberMap): void
  setSearchText(term: string): void
  onMemberAliasesSelected?(memberAliases: MemberAlias[]): void
  memberAliases?: MemberAlias[]
  disableAliasSelection?: boolean
}

export function SelectMembersView({
  selectedMembers,
  disabledMemberIds,
  recipients,
  isSingleSelectList = false,
  searchText,
  onMembersSelected,
  setSearchText,
  onMemberAliasesSelected,
  memberAliases,
  disableAliasSelection,
}: Props) {
  const styles = useStyles()

  // Track previous collapsed states before checking headers
  const [previousCollapsedStates, setPreviousCollapsedStates] = useState<
    Map<MemberAlias, boolean>
  >(new Map())

  const disabledMemberIdsSet = useMemo(
    () => new Set(disabledMemberIds),
    [disabledMemberIds]
  )

  // Cross-section disabling rules:
  // - If Crew header is checked, disable all members in New Hires section
  // - If New Hires header is checked, disable all new hire crew members in Crew section
  const crossSectionDisabledIdsSet = useMemo(() => {
    const set = new Set<Id>()
    if (!recipients || !memberAliases || memberAliases.length === 0) return set

    const { crewNewHires } = categorizeMembers(recipients)
    const selectedAliases = new Set<MemberAlias>(memberAliases)

    if (selectedAliases.has('allCrew') || selectedAliases.has('crewNewHires')) {
      crewNewHires.forEach(m => set.add(m.id))
    }

    return set
  }, [recipients, memberAliases])

  // Final disabled set combines incoming disabled ids with cross-section rules
  const effectiveDisabledIdsSet = useMemo(() => {
    const combined = new Set<Id>(disabledMemberIdsSet)
    crossSectionDisabledIdsSet.forEach(id => combined.add(id))
    return combined
  }, [disabledMemberIdsSet, crossSectionDisabledIdsSet])

  const onMemberAliasRemoved = useCallback(
    (memberAlias: MemberAlias) => {
      if (onMemberAliasesSelected && memberAliases) {
        const updatedMemberAliases = memberAliases.filter(
          alias => alias !== memberAlias
        )
        onMemberAliasesSelected(updatedMemberAliases)
      }
    },
    [memberAliases, onMemberAliasesSelected]
  )

  const onMemberToggled = useCallback(
    (member: Member) => {
      if (isSingleSelectList) {
        if (selectedMembers[member.id]) {
          onMembersSelected({})
        } else {
          onMembersSelected({ [member.id]: member })
        }
      } else {
        const updatedMembers = toggleItemInObjectMap(
          selectedMembers,
          member.id,
          member
        )

        onMembersSelected(updatedMembers)
      }
    },
    [isSingleSelectList, selectedMembers, onMembersSelected]
  )

  const isMemberAliasSelected = useCallback(
    (section: SectionListData<Member, Section>) =>
      memberAliases?.includes(section.type),
    [memberAliases]
  )

  // Helper function to get original members for a section type (not filtered by collapse state)
  const getMembersForSectionType = useCallback(
    (sectionType: MemberAlias): Member[] => {
      if (!recipients) return []

      const { managers, staff, guests, crew, others, crewNewHires } =
        categorizeMembers(recipients)

      switch (sectionType) {
        case 'allManagers':
          return managers
        case 'allStaff':
          return staff
        case 'allGuests':
          return guests
        case 'crewNewHires':
          return crewNewHires
        case 'allCrew':
          return crew
        case 'allAdmins':
          return others
        default:
          return []
      }
    },
    [recipients]
  )

  const onToggleSectionHeader = useCallback(
    (section: SectionListData<Member, Section>) => {
      const isMemberAliasCurrentlySelected = isMemberAliasSelected(section)

      if (isMemberAliasCurrentlySelected) {
        // Toggle OFF: Remove member alias from selected aliases
        if (onMemberAliasesSelected && memberAliases) {
          const updatedMemberAliases = memberAliases.filter(
            alias => alias !== section.type
          )
          onMemberAliasesSelected(updatedMemberAliases)
        }
      } else {
        // Toggle ON: Add member alias and remove all selected members from this section
        if (onMemberAliasesSelected) {
          const updatedMemberAliases = memberAliases
            ? [...memberAliases, section.type]
            : [section.type]
          onMemberAliasesSelected(updatedMemberAliases)
        }

        // Remove all selected members from this section
        const originalMembers = getMembersForSectionType(section.type)
        const updatedMembers = { ...selectedMembers }
        originalMembers.forEach(m => {
          // Do not remove currently disabled members (e.g., existing group members)
          if (selectedMembers[m.id] && !disabledMemberIdsSet.has(m.id)) {
            delete updatedMembers[m.id]
          }
        })
        onMembersSelected(updatedMembers)
      }
    },
    [
      selectedMembers,
      isMemberAliasSelected,
      onMembersSelected,
      onMemberAliasesSelected,
      memberAliases,
      getMembersForSectionType,
      disabledMemberIdsSet,
    ]
  )

  const sluggedMembers = useMemo(
    () =>
      Object.values(selectedMembers).filter(
        m => !effectiveDisabledIdsSet.has(m.id)
      ),
    [effectiveDisabledIdsSet, selectedMembers]
  )

  const handleCheckboxPress = useCallback(
    (
      section: SectionListData<Member, Section>,
      isCollapsed: boolean,
      onToggleCollapse: () => void
    ) => {
      const wasAliasSelected = isMemberAliasSelected(section)

      if (!wasAliasSelected) {
        // About to check: store current collapsed state
        setPreviousCollapsedStates(prev => {
          const newMap = new Map(prev)
          newMap.set(section.type, isCollapsed)
          return newMap
        })

        // If section is expanded, collapse it when checking
        if (!isCollapsed) {
          onToggleCollapse()
        }
      } else {
        // About to uncheck: restore previous collapsed state
        const previousState = previousCollapsedStates.get(section.type)
        if (previousState !== undefined && previousState !== isCollapsed) {
          // Only toggle if current state differs from previous state
          onToggleCollapse()
        }

        // Clean up stored state
        setPreviousCollapsedStates(prev => {
          const newMap = new Map(prev)
          newMap.delete(section.type)
          return newMap
        })
      }

      onToggleSectionHeader(section)
    },
    [
      isMemberAliasSelected,
      previousCollapsedStates,
      setPreviousCollapsedStates,
      onToggleSectionHeader,
    ]
  )

  const renderSectionHeader = useCallback(
    (
      section: SectionListData<Member, Section>,
      isCollapsed: boolean,
      onToggleCollapse: () => void
    ) => {
      const isMemberAliasCurrentlySelected = isMemberAliasSelected(section)

      const handleToggleCollapse = () => {
        // Only allow expansion if member alias is not selected
        if (!isMemberAliasCurrentlySelected) {
          onToggleCollapse()
        }
      }

      const shouldBeCollapsed = isCollapsed || isMemberAliasCurrentlySelected

      return (
        <CollapsibleSectionHeader
          title={section.title}
          isCollapsed={shouldBeCollapsed}
          onToggleCollapse={handleToggleCollapse}
          showCheckbox={!isSingleSelectList && !disableAliasSelection}
          isChecked={!!isMemberAliasSelected(section)}
          onCheckboxPress={() =>
            handleCheckboxPress(section, isCollapsed, onToggleCollapse)
          }
        />
      )
    },
    [
      isMemberAliasSelected,
      isSingleSelectList,
      disableAliasSelection,
      handleCheckboxPress,
    ]
  )

  const showMemberAliasSlugList = memberAliases && !isSingleSelectList

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <RecipientSearchBar
          searchText={searchText}
          setSearchText={setSearchText}
        />
        {showMemberAliasSlugList ? (
          <MemberAliasSlugList
            memberAliases={memberAliases}
            onAliasRemoved={onMemberAliasRemoved}
          />
        ) : null}
        {sluggedMembers.length > 0 && !isSingleSelectList && (
          <MemberSlugList
            members={sluggedMembers}
            onMemberRemoved={onMemberToggled}
          />
        )}

        <MembersList
          members={recipients}
          renderSectionHeader={renderSectionHeader}
          renderItem={({ item: member }) => (
            <MemberCard
              member={member}
              checked={!!selectedMembers[member.id]}
              disabled={effectiveDisabledIdsSet.has(member.id)}
              onPress={onMemberToggled}
              selectable
            />
          )}
          extraData={[selectedMembers, disabledMemberIds, memberAliases]}
          defaultCollapsedSections={memberAliases}
        />
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors?.white,
  },
}))
